import React, { useState, useEffect } from "react";
import axios from "axios";
import { addNewLearningTool } from "../../../utilities/learningZonesUtils";
import styles from "./clozeModal.module.css";

const apiUrl = process.env.REACT_APP_URL;

// Set Axios defaults
axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const ClozeModal = ({
  header,
  unitContent,
  approveBtnText,
  cancelBtnText,
  show,
  onClose,
  onConfirm,
  learningToolId,
  learningZoneId,
}) => {
  /* The cloze modal is used to enter details of a new cloze unit, as well as to edit an existing cloze unit. */

  const [unitText, setUnitText] = useState("");
  const [gapWord, setGapWord] = useState("");
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  /**
   * Split a given strings to words, and remove punctuation.
   * @param {*} text - the original string
   * @returns an array of words from the original string, without punctuation.
   */
  const getWords = (text) => {
    return text
      .split(/[\s]+/)
      .map((word) => word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ""));
  };

  useEffect(() => {
    if (show) {
      if (unitContent) {
        setUnitText(unitContent.learning_unit_text);
        const words = getWords(unitContent.learning_unit_text);
        setGapWord(words[unitContent.completion_word_num - 1] || "");
      } else {
        setUnitText("");
        setGapWord("");
        setError("");
      }
    } else {
      // Reset state when modal is closed
      if (unitContent) {
        setUnitText(unitContent.learning_unit_text);
        const words = getWords(unitContent.learning_unit_text);
        setGapWord(words[unitContent.completion_word_num - 1] || "");
        setError("");
      } else {
        setUnitText("");
        setGapWord("");
        setError("");
      }
    }
  }, [show, unitContent]);

  // Handle cloze text change
  const handleUnitTextChange = (e) => {
    setUnitText(e.target.value);
  };

  // Handle gap word change
  const handleGapWordChange = (e) => {
    setGapWord(e.target.value);
    setError("");
  };

  /**
   * Add a new unit to the tool.
   */
  const handleAddUnit = async () => {
    setShowError(true);

    let newErrorMsg = "";

    if (unitText.trim() === "") {
      newErrorMsg += "Please enter the text. ";
    }

    if (gapWord.trim() === "") {
      newErrorMsg += "Please enter the gap word.";
    }

    if (newErrorMsg) {
      setError(newErrorMsg);
      return;
    }

    const words = getWords(unitText);
    const gapWordIndex = words.indexOf(gapWord);

    if (gapWordIndex === -1) {
      setError(
        "The gap word must be a single, complete word from the sentence (excluding punctuation marks)."
      );
      return;
    }

    let toolId = learningToolId;

    if (!toolId) {
      /* This means a new learning tool is added - this is the first unit of the new tool. */
      try {
        toolId = await addNewLearningTool(learningZoneId, "cloze");
      } catch (error) {
        setError(error.message);
        return;
      }
    }

    const newUnitContent = {
      learningUnitText: unitText,
      completionWordNum: gapWordIndex + 1,
      unitSolutions: [
        {
          solutionText: gapWord,
        },
      ],
    };

    try {
      const reqUrl = `/learning_units/create/cloze`;

      const response = await axios.post(reqUrl, {
        learningToolId: toolId,
        learningUnitText: newUnitContent.learningUnitText,
        completionWordNum: newUnitContent.completionWordNum,
        unitSolutions: newUnitContent.unitSolutions,
      });

      if (response.status === 200 || response.status === 201) {
        newUnitContent.learning_unit_id = response.data.learning_unit_id;

        onConfirm({
          learning_tool_id: toolId,
          tool_type_id: "cloze",
          newUnit: {
            learning_unit_id: newUnitContent.learning_unit_id,
            learning_unit_text: unitText,
            completion_word_num: gapWordIndex + 1,
            unitSolutions: [{ solution_text: gapWord }],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_active: 1,
            learning_tool_id: learningToolId,
            unit_type: "cloze",
          },
        });
        setError("");
        setShowError(false);
      } else {
        setError("Failed to add the new learning unit.");
      }
    } catch (error) {
      setError("Failed to add the new learning unit.");
    }

    onClose();
  };

  /**
   * Handle confirmation of an edited unit. Validate the data, and if valid - update the database and the display.
   */
  const handleConfirm = async () => {
    setShowError(true);

    let newErrorMsg = "";

    if (unitText.trim() === "") {
      newErrorMsg += "Please enter the text. ";
    }

    if (gapWord.trim() === "") {
      newErrorMsg += "Please enter the gap word.";
    }

    if (newErrorMsg) {
      setError(newErrorMsg);
      return;
    }

    const words = getWords(unitText);
    const gapWordIndex = words.indexOf(gapWord);

    if (gapWordIndex === -1) {
      setError(
        "The gap word must be a single, complete word from the sentence (excluding punctuation marks)."
      );
      return;
    }

    const updatedUnitContent = {
      learningUnitText: unitText,
      completionWordNum: gapWordIndex + 1,
      unitSolutions: [
        {
          solutionText: gapWord,
        },
      ],
    };

    try {
      const learningUnitId = unitContent.learning_unit_id;
      const reqUrl = `/learning_units/update/cloze/${learningUnitId}`;

      const response = await axios.put(reqUrl, {
        learningToolId,
        learningUnitText: updatedUnitContent.learningUnitText,
        completionWordNum: updatedUnitContent.completionWordNum,
        unitSolutions: updatedUnitContent.unitSolutions,
      });

      if (response.status === 200) {
        onConfirm({
          learning_unit_text: unitText,
          completion_word_num: gapWordIndex + 1,
          unitSolutions: [{ solution_text: gapWord }],
        });

        setError("");
        setShowError(false);
      } else {
        setError("Failed to update the learning unit.");
      }
    } catch (error) {
      setError("Failed to update the learning unit.");
    }

    onClose();
  };

  /**
   * Reset the modal fields and error message
   */
  const resetState = () => {
    if (unitContent) {
      setUnitText(unitContent.learning_unit_text);
      const words = getWords(unitContent.learning_unit_text);
      setGapWord(words[unitContent.completion_word_num - 1] || "");
      setError("");
      setShowError(false);
    } else {
      setUnitText("");
      setGapWord("");
      setError("");
      setShowError(false);
    }
  };

  /* Close the modal when clicking outside it */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetState();
      onClose();
    }
  };

  /* Close the modal when clicking on the close button */
  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
        <h2>{header}</h2>
        <div className={styles.field}>
          <label>Unit Text</label>
          <textarea value={unitText} onChange={handleUnitTextChange}></textarea>
        </div>
        <div className={styles.field}>
          <label>Gap Word</label>
          <input type="text" value={gapWord} onChange={handleGapWordChange} />
          <p
            className={`${styles.error} ${
              error && showError ? styles.visible : ""
            }`}
          >
            {error}
          </p>
        </div>
        <div className={styles.actions}>
          <button onClick={handleClose}>{cancelBtnText}</button>
          {!unitContent && (
            <button onClick={handleAddUnit}>{approveBtnText}</button>
          )}
          {unitContent && (
            <button onClick={handleConfirm}>{approveBtnText}</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClozeModal;
